import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Ban,
  Eye,
  Loader2,
  ShieldAlert,
  Stethoscope,
} from 'lucide-react';
import { LoadingSpinner } from '../components/LoadingSpinner';
import {
  adminListAgentInstances,
  adminSetInstanceBanned,
  adminInstanceHealthCheck,
  type AdminAgentInstanceRow,
} from '../utils/agentApi';
import { parseAdminStaffSession } from '../utils/adminSession';
import { AdminPageToolbar } from '../components/admin/AdminLayout';

const PAGE = 50;

export const AgentInstancesPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState<AdminAgentInstanceRow[]>([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [error, setError] = useState<string | null>(null);

  const [detailRow, setDetailRow] = useState<AdminAgentInstanceRow | null>(null);
  const [healthRow, setHealthRow] = useState<AdminAgentInstanceRow | null>(null);
  const [healthLoading, setHealthLoading] = useState(false);
  const [healthReport, setHealthReport] = useState<Record<string, unknown> | null>(null);
  const [healthError, setHealthError] = useState<string | null>(null);

  const [banRow, setBanRow] = useState<AdminAgentInstanceRow | null>(null);
  const [banReason, setBanReason] = useState('');
  const [banSubmitting, setBanSubmitting] = useState(false);

  const parsedAccess = parseAdminStaffSession();
  const isSuperAdmin = parsedAccess?.role === 'superadmin';

  useEffect(() => {
    if (!isSuperAdmin) {
      navigate('/admin/agents', { replace: true });
    }
  }, [isSuperAdmin, navigate]);

  const load = useCallback(async () => {
    if (!parsedAccess?.hy_user_id?.trim()) {
      setError('Sign in with Supabase as superadmin and reload.');
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    const { ok, data } = await adminListAgentInstances({
      offset,
      limit: PAGE,
    });
    const payload = data as {
      success?: boolean;
      instances?: AdminAgentInstanceRow[];
      total?: number;
      error?: string;
    };
    if (!ok || !payload?.success) {
      setError(typeof payload?.error === 'string' ? payload.error : 'Failed to load instances');
      setRows([]);
      setTotal(0);
    } else {
      setRows((payload.instances || []) as AdminAgentInstanceRow[]);
      setTotal(typeof payload.total === 'number' ? payload.total : 0);
    }
    setLoading(false);
  }, [parsedAccess?.hy_user_id, offset]);

  useEffect(() => {
    if (!isSuperAdmin) return;
    void load();
  }, [isSuperAdmin, load]);

  useEffect(() => {
    if (!healthRow || !parsedAccess?.hy_user_id?.trim()) {
      setHealthReport(null);
      setHealthError(null);
      setHealthLoading(false);
      return;
    }
    let cancelled = false;
    setHealthLoading(true);
    setHealthError(null);
    setHealthReport(null);
    void (async () => {
      const { ok, data } = await adminInstanceHealthCheck({
        instanceId: healthRow.id,
      });
      const payload = data as {
        success?: boolean;
        error?: string;
        message?: string;
        report?: Record<string, unknown>;
      };
      if (cancelled) return;
      setHealthLoading(false);
      if (!ok) {
        setHealthError(typeof payload?.error === 'string' ? payload.error : 'Request failed');
        return;
      }
      if (payload?.error === 'insufficient_history') {
        setHealthError(payload.message || 'Not enough messages to score.');
        return;
      }
      if (payload?.report && typeof payload.report === 'object') {
        setHealthReport(payload.report);
      } else {
        setHealthError('Unexpected response');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [healthRow, parsedAccess?.hy_user_id]);

  const submitBan = async () => {
    if (!banRow || !parsedAccess?.hy_user_id?.trim()) return;
    setBanSubmitting(true);
    try {
      const banned = banRow.status !== 'banned';
      const { ok, data } = await adminSetInstanceBanned({
        instanceId: banRow.id,
        banned,
        reason: banReason.trim() || undefined,
      });
      const payload = data as { error?: string };
      if (!ok) {
        alert(payload?.error || 'Could not update instance');
        return;
      }
      setBanRow(null);
      setBanReason('');
      void load();
    } finally {
      setBanSubmitting(false);
    }
  };

  if (!isSuperAdmin) {
    return null;
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col bg-base text-th-primary">
      <AdminPageToolbar
        maxWidthClass="max-w-7xl"
        title="User agent instances"
        subtitle="Superadmin — list, health, and ban controls"
      >
        <button
          type="button"
          onClick={() => navigate('/admin/agents')}
          className="button-secondary text-sm"
        >
          All agents
        </button>
      </AdminPageToolbar>

      <div className="min-h-0 flex-1 overflow-y-auto max-w-7xl mx-auto w-full px-4 sm:px-6 py-4 space-y-6">
        <div className="rounded-xl border border-subtle bg-elevated/80 p-6 shadow-lg">
          <p className="text-sm text-th-secondary max-w-3xl flex items-start gap-2">
            <ShieldAlert className="h-5 w-5 text-status-warning shrink-0 mt-0.5" />
            <span>
            Lists every onboarded instance with onboarding answers, synthesized instructions, file and message counts.
            Ban enforces a user reset (clear chat + files) before they can chat again.
            </span>
          </p>

          {error && (
            <div className="mt-4 rounded-lg border border-status-error/40 bg-status-error/10 px-3 py-2 text-sm text-status-error">
              {error}
            </div>
          )}

          {loading ? (
            <div className="flex justify-center py-16">
              <LoadingSpinner />
            </div>
          ) : (
            <>
              <div className="mt-6 overflow-x-auto rounded-lg border border-subtle">
                <table className="w-full text-left text-sm">
                  <thead className="bg-overlay/50 text-th-secondary">
                    <tr>
                      <th className="px-3 py-2 font-medium">Template</th>
                      <th className="px-3 py-2 font-medium">User</th>
                      <th className="px-3 py-2 font-medium">Status</th>
                      <th className="px-3 py-2 font-medium tabular-nums">Msgs</th>
                      <th className="px-3 py-2 font-medium tabular-nums">Files</th>
                      <th className="px-3 py-2 font-medium">Updated</th>
                      <th className="px-3 py-2 font-medium text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.length === 0 ? (
                      <tr>
                        <td colSpan={7} className="px-3 py-8 text-center text-th-secondary">
                          No instances found.
                        </td>
                      </tr>
                    ) : (
                      rows.map((r) => (
                        <tr key={r.id} className="border-t border-subtle/80 hover:bg-overlay/30">
                          <td className="px-3 py-2 max-w-[200px] truncate" title={r.template_name ?? ''}>
                            {r.template_name ?? r.agent_template_id}
                          </td>
                          <td className="px-3 py-2 max-w-[220px]">
                            <div
                              className="font-medium text-th-primary truncate"
                              title={r.hy_name || r.hy_user_id}
                            >
                              {r.hy_name?.trim() || '—'}
                            </div>
                            <div
                              className="font-mono text-[11px] text-th-tertiary truncate"
                              title={r.hy_user_id}
                            >
                              {r.hy_user_id}
                            </div>
                          </td>
                          <td className="px-3 py-2">
                            <span
                              className={
                                r.status === 'banned'
                                  ? 'text-status-error font-medium'
                                  : 'text-th-secondary'
                              }
                            >
                              {r.status}
                            </span>
                          </td>
                          <td className="px-3 py-2 tabular-nums">{r.message_count}</td>
                          <td className="px-3 py-2 tabular-nums">{r.file_count}</td>
                          <td className="px-3 py-2 text-xs text-th-tertiary whitespace-nowrap">
                            {r.updated_at ? new Date(r.updated_at).toLocaleString() : '—'}
                          </td>
                          <td className="px-3 py-2 text-right whitespace-nowrap">
                            <button
                              type="button"
                              className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs font-medium text-th-secondary hover:bg-overlay hover:text-th-primary mr-1"
                              onClick={() => setDetailRow(r)}
                            >
                              <Eye className="h-3.5 w-3.5" />
                              Details
                            </button>
                            <button
                              type="button"
                              className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs font-medium text-th-secondary hover:bg-overlay hover:text-th-primary mr-1"
                              onClick={() => setHealthRow(r)}
                            >
                              <Stethoscope className="h-3.5 w-3.5" />
                              Health
                            </button>
                            <button
                              type="button"
                              className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs font-medium text-status-error hover:bg-status-error/10"
                              onClick={() => {
                                setBanRow(r);
                                setBanReason('');
                              }}
                            >
                              <Ban className="h-3.5 w-3.5" />
                              {r.status === 'banned' ? 'Unban' : 'Ban'}
                            </button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>

              {total > PAGE && (
                <div className="mt-4 flex items-center justify-between text-sm text-th-secondary">
                  <span>
                    Showing {offset + 1}–{Math.min(offset + PAGE, total)} of {total}
                  </span>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      disabled={offset === 0}
                      className="rounded-md border border-subtle px-3 py-1 disabled:opacity-40"
                      onClick={() => setOffset((o) => Math.max(0, o - PAGE))}
                    >
                      Previous
                    </button>
                    <button
                      type="button"
                      disabled={offset + PAGE >= total}
                      className="rounded-md border border-subtle px-3 py-1 disabled:opacity-40"
                      onClick={() => setOffset((o) => o + PAGE)}
                    >
                      Next
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {detailRow && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60" role="dialog">
          <div className="max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-xl border border-subtle bg-elevated p-6 shadow-2xl">
            <h2 className="text-lg font-semibold text-th-primary">Instance details</h2>
            <p className="text-xs text-th-tertiary font-mono mt-1 break-all">{detailRow.id}</p>
            <div className="mt-4 space-y-3 text-sm">
              <div>
                <div className="text-th-secondary text-xs uppercase tracking-wide">Onboarding answers</div>
                <pre className="mt-1 max-h-48 overflow-auto rounded-lg bg-overlay/50 p-3 text-xs text-th-primary whitespace-pre-wrap">
                  {JSON.stringify(detailRow.onboarding_answers ?? {}, null, 2)}
                </pre>
              </div>
              <div>
                <div className="text-th-secondary text-xs uppercase tracking-wide">Custom instructions</div>
                <pre className="mt-1 max-h-64 overflow-auto rounded-lg bg-overlay/50 p-3 text-xs text-th-primary whitespace-pre-wrap">
                  {(detailRow.custom_instructions || '').trim() || '—'}
                </pre>
              </div>
              {detailRow.banned_reason ? (
                <div className="rounded-lg border border-status-error/30 bg-status-error/10 px-3 py-2 text-status-error text-xs">
                  Ban note: {detailRow.banned_reason}
                </div>
              ) : null}
            </div>
            <button
              type="button"
              className="mt-6 w-full rounded-lg border border-subtle py-2 text-sm font-medium text-th-primary hover:bg-overlay"
              onClick={() => setDetailRow(null)}
            >
              Close
            </button>
          </div>
        </div>
      )}

      {healthRow && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60" role="dialog">
          <div className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-xl border border-subtle bg-elevated p-6 shadow-2xl">
            <h2 className="text-lg font-semibold text-th-primary flex items-center gap-2">
              <Stethoscope className="h-5 w-5 text-accent" />
              Health check
            </h2>
            <p className="text-xs text-th-tertiary font-mono mt-1 break-all">{healthRow.id}</p>
            {healthLoading ? (
              <div className="flex items-center justify-center gap-2 py-12 text-th-secondary">
                <Loader2 className="h-6 w-6 animate-spin" />
                Analyzing transcript…
              </div>
            ) : healthError ? (
              <p className="mt-4 text-sm text-status-error">{healthError}</p>
            ) : healthReport ? (
              <ul className="mt-4 space-y-2 text-sm text-th-secondary">
                {typeof healthReport.helpfulness_score === 'number' && (
                  <li>Helpfulness: {healthReport.helpfulness_score}/5</li>
                )}
                {typeof healthReport.value_score === 'number' && (
                  <li>Value: {healthReport.value_score}/5</li>
                )}
                {typeof healthReport.hallucination_risk === 'number' && (
                  <li>Hallucination risk: {healthReport.hallucination_risk}/5</li>
                )}
                {typeof healthReport.satisfaction_proxy === 'number' && (
                  <li>Satisfaction (proxy): {healthReport.satisfaction_proxy}/5</li>
                )}
                {typeof healthReport.clarity_score === 'number' && (
                  <li>Clarity: {healthReport.clarity_score}/5</li>
                )}
                {Array.isArray(healthReport.summary_bullets) && (
                  <li className="pt-2">
                    <span className="text-th-primary font-medium">Summary</span>
                    <ul className="mt-1 list-disc pl-4">
                      {(healthReport.summary_bullets as string[]).map((b, i) => (
                        <li key={i}>{b}</li>
                      ))}
                    </ul>
                  </li>
                )}
                {typeof healthReport.strengths === 'string' && (
                  <li className="pt-2">
                    <span className="text-th-primary font-medium">Strengths</span>
                    <p className="mt-1">{healthReport.strengths}</p>
                  </li>
                )}
                {typeof healthReport.risks === 'string' && (
                  <li>
                    <span className="text-th-primary font-medium">Risks</span>
                    <p className="mt-1">{healthReport.risks}</p>
                  </li>
                )}
              </ul>
            ) : null}
            <button
              type="button"
              className="mt-6 w-full rounded-lg border border-subtle py-2 text-sm font-medium text-th-primary hover:bg-overlay"
              onClick={() => setHealthRow(null)}
            >
              Close
            </button>
          </div>
        </div>
      )}

      {banRow && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60" role="dialog">
          <div className="w-full max-w-md rounded-xl border border-subtle bg-elevated p-6 shadow-2xl">
            <h2 className="text-lg font-semibold text-th-primary">
              {banRow.status === 'banned' ? 'Force unban instance' : 'Ban instance'}
            </h2>
            <p className="mt-2 text-sm text-th-secondary">
              {banRow.status === 'banned'
                ? 'Sets status to active immediately (support). Users can also clear chat + files and reinstate themselves.'
                : 'User chat and uploads are blocked until they clear messages and files, then tap Continue.'}
            </p>
            {banRow.status !== 'banned' && (
              <label className="mt-4 block text-sm">
                <span className="text-th-secondary">Reason (optional)</span>
                <textarea
                  className="mt-1 w-full rounded-lg border border-subtle bg-base px-3 py-2 text-sm text-th-primary"
                  rows={3}
                  value={banReason}
                  onChange={(e) => setBanReason(e.target.value)}
                  placeholder="Internal note"
                />
              </label>
            )}
            <div className="mt-6 flex gap-2">
              <button
                type="button"
                className="flex-1 rounded-lg border border-subtle py-2 text-sm"
                onClick={() => {
                  setBanRow(null);
                  setBanReason('');
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={banSubmitting}
                className="flex-1 rounded-lg bg-brand py-2 text-sm font-semibold text-surface disabled:opacity-50"
                onClick={() => void submitBan()}
              >
                {banSubmitting ? '…' : banRow.status === 'banned' ? 'Unban' : 'Ban'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
